// Lecteur PDF natif, générique et réutilisable (13/10). Moteur : Mozilla PDF.js 6.4.299 (vendor-pdfjs, Apache 2.0), API
// officielle seulement (getDocument, getPage, getViewport, render, TextLayer). Interface : la nôtre (Button et DropdownMenu
// UBM, champ natif), aucune barre d'outils tierce, aucune iframe. Aucune donnée métier ici : l'appelant fournit l'URL, le
// titre, le retour, le téléchargement éventuel et la reprise de position (onPosition / initialPage).
// - Lecture continue : toutes les pages en colonne, chacune dans un cadre à sa taille ; la toile (canvas) et la couche de texte
//   (sélection, lecteurs d'écran) ne sont rendues que près de l'écran (IntersectionObserver), et toute peinture en cours est
//   annulée au changement de zoom ou au démontage ; le document est détruit au démontage (loadingTask.destroy).
// - Nombre de pages réel, saut à une page (champ natif), zoom réel (nouveau rendu) qui garde le point lu au centre.
// - Barre du haut et outils du bas : masqués quand on descend, revenus quand on remonte ou sur un tap ; aucune minuterie.
// - Chargement asynchrone réel ; erreur avec « Réessayer » (nouvelle tentative réelle) ; mouvement réduit : opacité seulement.
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type FormEvent, type PointerEvent } from "react";
import { ChevronLeft, Download, Minus, MoreHorizontal, Plus } from "lucide-react";
import { Button } from "@ubm/ui/components/button";
import { DropdownMenuContentBase, DropdownMenuItem, DropdownMenuPortal, DropdownMenuRoot, DropdownMenuTrigger, dropdownMenuClassNames as menu } from "@ubm/ui/components/dropdown-menu";
// @ts-expect-error — official PDF.js ES module, no typings in the local copy (types live in the npm package).
import * as pdfjs from "../vendor-pdfjs/pdf.min.mjs";

// The worker is the pinned official file, copied next to the bundle's assets by build.mjs.
pdfjs.GlobalWorkerOptions.workerSrc = new URL("./assets/pdf.worker.min.mjs", import.meta.url).href;

export interface PdfReaderProps {
  /** URL of a real PDF file. */
  url: string;
  title: string;
  onBack: () => void;
  /** Only when the real file may be downloaded: shows the menu with « Télécharger ». */
  downloadUrl?: string;
  downloadName?: string;
  /** Page to open at (resume); the caller owns where it is kept. */
  initialPage?: number;
  /** Called with the page currently read. */
  onPosition?: (page: number) => void;
}

type Size = { w: number; h: number };
const ZOOMS = [1, 1.25, 1.5, 2];
const GAP = 12;
const SIDE = 16;

/** Paints one page of a loaded document into `canvas` (device-pixel sharp) and, when given, its official text layer into
 *  `text`. Returns a cancel function (render and text layer), shared by the reader and the preview. Only a cancellation of
 *  OUR OWN paint (cancel() called, or PDF.js RenderingCancelledException) is silent; any other failure reaches `onError`. */
function paintPage(doc: any, index: number, scale: number, canvas: HTMLCanvasElement, text: HTMLDivElement | null, onError: (error: unknown) => void): () => void {
  let cancelled = false; let renderTask: any = null; let textLayer: any = null;
  (async () => {
    const page = await doc.getPage(index + 1);
    if (cancelled) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 3);
    const view = page.getViewport({ scale });
    canvas.width = Math.floor(view.width * dpr); canvas.height = Math.floor(view.height * dpr);
    canvas.style.width = `${view.width}px`; canvas.style.height = `${view.height}px`;
    renderTask = page.render({ canvas, viewport: page.getViewport({ scale: scale * dpr }) });
    await renderTask.promise;
    if (cancelled || !text) return;
    text.replaceChildren();
    textLayer = new pdfjs.TextLayer({ textContentSource: page.streamTextContent(), container: text, viewport: view });
    await textLayer.render();
  })().catch((error: any) => { if (cancelled || error?.name === "RenderingCancelledException") return; onError(error); });
  return () => { cancelled = true; renderTask?.cancel(); textLayer?.cancel(); };
}

/** Loads a real document: status, document, page sizes at scale 1, and a real retry (a new attempt destroys the old task). */
function usePdfDocument(url: string) {
  const [attempt, setAttempt] = useState(0);
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");
  const [doc, setDoc] = useState<any>(null);
  const [sizes, setSizes] = useState<Size[]>([]);
  useEffect(() => {
    let alive = true;
    setStatus("loading"); setDoc(null); setSizes([]);
    const task = pdfjs.getDocument({ url });
    task.promise.then(async (d: any) => {
      const s: Size[] = [];
      for (let i = 1; i <= d.numPages; i++) { const p = await d.getPage(i); const v = p.getViewport({ scale: 1 }); s.push({ w: v.width, h: v.height }); }
      if (!alive) return;
      setDoc(d); setSizes(s); setStatus("ready");
    }).catch(() => { if (alive) setStatus("error"); });
    return () => { alive = false; void task.destroy(); };
  }, [url, attempt]);
  // `fail`: a page that cannot be painted puts the document in the error state; `retry` reloads it and repaints. Both are
  // stable (useCallback), so passing them to pages never re-triggers a paint.
  const retry = useCallback(() => setAttempt(n => n + 1), []);
  const fail = useCallback(() => setStatus("error"), []);
  return { status, doc, sizes, retry, fail };
}

/** One page: a frame at its final size; canvas + text layer painted only when near the viewport. */
function PdfPage({ doc, index, scale, size, root, onError }: { doc: any; index: number; scale: number; size: Size; root: HTMLElement | null; onError: (error: unknown) => void }) {
  const frame = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const text = useRef<HTMLDivElement>(null);
  const [near, setNear] = useState(false);
  useEffect(() => {
    const el = frame.current; if (!el || !root) return;
    const io = new IntersectionObserver(entries => setNear(entries.some(e => e.isIntersecting)), { root, rootMargin: "150% 0px" });
    io.observe(el); return () => io.disconnect();
  }, [root]);
  useEffect(() => {
    if (!near || !canvas.current) return;
    return paintPage(doc, index, scale, canvas.current, text.current, onError);
  }, [near, doc, index, scale, onError]);
  return <div ref={frame} className="cc-pdf-page" data-page={index + 1}
    style={{ width: size.w * scale, height: size.h * scale, ["--total-scale-factor" as string]: String(scale) }}>
    <canvas ref={canvas} aria-hidden="true" />
    <div ref={text} className="textLayer" />
  </div>;
}

export interface PdfPreviewProps {
  url: string;
  /** Accessible name of the preview image (the document's title). */
  title: string;
  /** Called once with the real page count read from the document. */
  onLoad?: (pageCount: number) => void;
}

/** First page of a real document, fitted to its container width, painted by the same engine (no text layer: an image). */
export function PdfPreview({ url, title, onLoad }: PdfPreviewProps) {
  const { status, doc, sizes, retry, fail } = usePdfDocument(url);
  const box = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const [width, setWidth] = useState(0);
  const onLoadRef = useRef(onLoad); onLoadRef.current = onLoad;
  useLayoutEffect(() => {
    const el = box.current; if (!el) return;
    // Content width = clientWidth minus the box's own horizontal padding (the canvas lives inside the padding).
    const measure = () => { const cs = getComputedStyle(el); setWidth(Math.max(0, el.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight))); };
    const ro = new ResizeObserver(measure); ro.observe(el); return () => ro.disconnect();
  }, []);
  useEffect(() => { if (status === "ready") onLoadRef.current?.(sizes.length); }, [status, sizes.length]);
  const first = sizes[0];
  const scale = first && width ? width / first.w : 0;
  useEffect(() => {
    if (status !== "ready" || !doc || !scale || !canvas.current) return;
    return paintPage(doc, 0, scale, canvas.current, null, fail);
  }, [status, doc, scale]);
  return <div ref={box} className="cc-pdf-preview" aria-busy={status === "loading" || undefined}>
    {status === "loading" && <p className="cc-pdf-state" role="status">Chargement du document…</p>}
    {status === "error" && <div className="cc-pdf-state" role="alert"><p>Impossible d'ouvrir le document.</p>
      <Button variant="secondary" size="md" onClick={retry}>Réessayer</Button></div>}
    {status === "ready" && first && scale > 0 && <div className="cc-pdf-page" style={{ width: first.w * scale, height: first.h * scale }}>
      <canvas ref={canvas} role="img" aria-label={`Aperçu de la première page : ${title}`} />
    </div>}
  </div>;
}

export function PdfReader({ url, title, onBack, downloadUrl, downloadName, initialPage, onPosition }: PdfReaderProps) {
  const scroller = useRef<HTMLDivElement>(null);
  const { status, doc, sizes, retry, fail } = usePdfDocument(url);
  const [fit, setFit] = useState(0);
  const [zoom, setZoom] = useState(0);
  const [page, setPage] = useState(initialPage ?? 1);
  const [jump, setJump] = useState(String(initialPage ?? 1));
  const [hidden, setHidden] = useState(false);
  const lastTop = useRef(0);
  /** Becomes true on the first real user gesture in the pages (pointer, wheel, touch, key); programmatic scrolls before it
   *  (resume, layout) never hide the bars. */
  const interacted = useRef(false);
  const markInteraction = () => { interacted.current = true; };
  const anchor = useRef<number | null>(null);
  const resumed = useRef(false);
  const onPositionRef = useRef(onPosition); onPositionRef.current = onPosition;

  // Fit width = the reading column, measured (no fixed device width).
  useLayoutEffect(() => {
    const el = scroller.current; if (!el) return;
    const ro = new ResizeObserver(() => setFit(Math.max(0, el.clientWidth - SIDE * 2)));
    ro.observe(el); return () => ro.disconnect();
  }, []);

  const scaleOf = (s: Size) => (fit && s.w ? fit / s.w : 1) * ZOOMS[zoom]!;
  const tops = (() => { let y = GAP; return sizes.map(s => { const t = y; y += s.h * scaleOf(s) + GAP; return t; }); })();

  // Zoom keeps the point read at the centre of the screen.
  const changeZoom = (next: number) => {
    const el = scroller.current;
    if (el && el.scrollHeight) anchor.current = (el.scrollTop + el.clientHeight / 2) / el.scrollHeight;
    setZoom(Math.max(0, Math.min(ZOOMS.length - 1, next)));
  };
  useLayoutEffect(() => {
    const el = scroller.current;
    if (el && anchor.current !== null) { el.scrollTop = anchor.current * el.scrollHeight - el.clientHeight / 2; anchor.current = null; }
  }, [zoom]);

  const goTo = (n: number) => {
    const el = scroller.current; const i = Math.max(1, Math.min(sizes.length, n)) - 1;
    if (el && tops[i] !== undefined) el.scrollTo({ top: tops[i]! - GAP });
  };
  // Resume once the pages are laid out.
  useLayoutEffect(() => {
    if (status !== "ready" || !fit || resumed.current) return;
    resumed.current = true;
    if ((initialPage ?? 1) > 1) goTo(initialPage!);
  });

  const onScroll = () => {
    const el = scroller.current; if (!el) return;
    const top = el.scrollTop, delta = top - lastTop.current;
    if (interacted.current) { if (delta > 8 && top > 40) setHidden(true); else if (delta < -8) setHidden(false); }
    lastTop.current = top;
    const probe = top + el.clientHeight * 0.3;
    let current = 1; tops.forEach((t, i) => { if (t <= probe) current = i + 1; });
    if (current !== page) { setPage(current); setJump(String(current)); onPositionRef.current?.(current); }
  };
  // A tap (no drag) on the pages brings the bars back or hides them.
  const press = useRef<{ x: number; y: number } | null>(null);
  const down = (e: PointerEvent) => { markInteraction(); press.current = { x: e.clientX, y: e.clientY }; };
  const up = (e: PointerEvent) => {
    const p = press.current; press.current = null;
    if (p && Math.abs(e.clientX - p.x) < 6 && Math.abs(e.clientY - p.y) < 6 && !window.getSelection()?.toString()) setHidden(h => !h);
  };
  const submitJump = (e: FormEvent) => { e.preventDefault(); const n = parseInt(jump, 10); if (Number.isFinite(n)) goTo(n); };
  const download = () => { if (!downloadUrl) return; const a = document.createElement("a"); a.href = downloadUrl; a.download = downloadName ?? ""; a.click(); };

  // Keyboard: focus entering the bars (Tab) brings them back, so no focused control is ever hidden (CSS :focus-within too).
  return <div className="cc-pdf" data-hidden={hidden || undefined} onFocusCapture={e => { if ((e.target as HTMLElement).closest(".cc-pdf-bar, .cc-pdf-tools")) setHidden(false); }}>
    <header className="cc-pdf-bar">
      <button type="button" className="cc-icon-btn" aria-label="Retour" onClick={onBack}><ChevronLeft size={18} strokeWidth={2} /></button>
      <p className="cc-pdf-title" title={title}>{title}</p>
      {downloadUrl && <DropdownMenuRoot>
        <DropdownMenuTrigger asChild><button type="button" className="cc-icon-btn" aria-label="Plus d'options"><MoreHorizontal size={18} strokeWidth={2} /></button></DropdownMenuTrigger>
        <DropdownMenuPortal><DropdownMenuContentBase className={`${menu.menu} cc-pdf-menu`} side="bottom" align="end" sideOffset={6} collisionPadding={12} aria-label="Options du document">
          <DropdownMenuItem className={menu.item} onSelect={download}><span className={menu.icon} aria-hidden="true"><Download size={16} strokeWidth={1.75} /></span>Télécharger</DropdownMenuItem>
        </DropdownMenuContentBase></DropdownMenuPortal>
      </DropdownMenuRoot>}
    </header>

    <div ref={scroller} className="cc-pdf-scroll" onScroll={onScroll} onPointerDown={down} onPointerUp={up} onWheel={markInteraction} onTouchStart={markInteraction} onKeyDown={markInteraction} aria-busy={status === "loading" || undefined}>
      {status === "loading" && <p className="cc-pdf-state" role="status">Chargement du document…</p>}
      {status === "error" && <div className="cc-pdf-state" role="alert"><p>Impossible d'ouvrir le document.</p>
        <Button variant="secondary" size="md" onClick={retry}>Réessayer</Button></div>}
      {status === "ready" && doc && fit > 0 && <div className="cc-pdf-pages" role="document" aria-label={title}>
        {sizes.map((s, i) => <PdfPage key={i} doc={doc} index={i} scale={scaleOf(s)} size={s} root={scroller.current} onError={fail} />)}
      </div>}
    </div>

    {status === "ready" && <div className="cc-pdf-tools">
      <form className="cc-pdf-jump" onSubmit={submitJump}>
        <label className="cc-pdf-sr" htmlFor="cc-pdf-page">Aller à la page</label>
        <input id="cc-pdf-page" type="number" inputMode="numeric" min={1} max={sizes.length} value={jump} onChange={e => setJump(e.target.value)} />
        <span aria-hidden="true">sur {sizes.length}</span>
      </form>
      <span className="cc-pdf-sr" role="status">Page {page} sur {sizes.length}</span>
      <div className="cc-pdf-zoom">
        <Button variant="secondary" size="sm" shape="icon" aria-label="Réduire" disabled={zoom === 0} onClick={() => changeZoom(zoom - 1)}><Minus size={16} /></Button>
        <span className="cc-pdf-zoomv">{Math.round(ZOOMS[zoom]! * 100)} %</span>
        <Button variant="secondary" size="sm" shape="icon" aria-label="Agrandir" disabled={zoom === ZOOMS.length - 1} onClick={() => changeZoom(zoom + 1)}><Plus size={16} /></Button>
      </div>
    </div>}
  </div>;
}
